// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { OnboardingQuestionAnswer } from '@shared/data-access/interfaces/api/brokerage/client-types';
import type { TFunction } from 'i18next';
import type { ProfilingFormData } from '../types';
import { ALL_ONBOARDING_QUESTIONS, OnboardingQuestionBlock } from '../types';

export const buildOnboardingQA = (
  data: Omit<ProfilingFormData, 'personalData' | 'resultAndSignature'>,
  translate: TFunction,
): Record<string, OnboardingQuestionAnswer> => {
  const answers = Object.values(data).flatMap(questions =>
    Object.entries(questions ?? {}),
  );

  const qa = answers
    .filter(([id]) => ALL_ONBOARDING_QUESTIONS.has(id))
    .map(([id, value]) => {
      const question = ALL_ONBOARDING_QUESTIONS.get(id)!;

      const answer = question.options.find(option => option.value === value)!;

      return [
        id,
        {
          answer_label: translate(...answer.label),
          id: id,
          question_label: translate(...question.label),
          type: question.types,
          value,
          scores: answer.score,
        } as OnboardingQuestionAnswer,
      ] as [string, OnboardingQuestionAnswer];
    });

  return Object.fromEntries(qa);
};

export const buildOnboardingFormData = (
  qa: Record<string, OnboardingQuestionAnswer>,
): Omit<ProfilingFormData, 'personalData' | 'resultAndSignature'> => {
  const answers = Object.values(qa);

  const formData = [
    {
      id: 'investmentObjectives',
      block: OnboardingQuestionBlock.InvestmentObjectives,
    },
    {
      id: 'financialKnowledge',
      block: OnboardingQuestionBlock.FinancialKnowledge,
    },
    {
      id: 'investmentExperience',
      block: OnboardingQuestionBlock.InvestmentExperience,
    },
    {
      id: 'financialSituation',
      block: OnboardingQuestionBlock.FinancialSituation,
    },
    {
      id: 'sustainability',
      block: OnboardingQuestionBlock.Sustainability,
    },
  ].map(section => {
    const values = answers
      .filter(
        value =>
          ALL_ONBOARDING_QUESTIONS.has(value.id) &&
          ALL_ONBOARDING_QUESTIONS.get(value.id)?.block === section.block,
      )
      .map(value => {
        const question = ALL_ONBOARDING_QUESTIONS.get(value.id)!;

        return [question.id, value.value];
      });

    return [section.id, Object.fromEntries(values)];
  });

  return Object.fromEntries(formData);
};
